// src/screens/BoardScreen.tsx - 掲示板画面（設定機能完全版）

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  Add,
  ThumbUp,
  ChatBubbleOutline,
  Visibility,
  Send,
  Settings
} from '@mui/icons-material';
import {
  getThreads,
  getThreadById,
  createThread,
  createComment,
  getComments,
  likeThread,
  likeComment,
  hasLikedThread,
  hasLikedComment,
  getBoardSettings,
  setBoardSettings,
  Thread,
  Comment,
  ThreadCategory,
  BoardSettings
} from '../services/boardService';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

function BoardScreen({ onShowSnackbar }: Props) {
  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [showNewThread, setShowNewThread] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [likedThreads, setLikedThreads] = useState<Set<string>>(new Set());
  const [likedComments, setLikedComments] = useState<Set<string>>(new Set());
  const [boardSettings, setBoardSettingsState] = useState<BoardSettings>({
    userName: '',
    region: '',
    defaultAnonymous: false
  });

  // 設定用の状態
  const [tempUserName, setTempUserName] = useState('');
  const [tempRegion, setTempRegion] = useState('');
  const [tempDefaultAnonymous, setTempDefaultAnonymous] = useState(false);

  // 新規投稿
  const [newCategory, setNewCategory] = useState<ThreadCategory>('chat');
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);

  // 日本の都道府県リスト
  const regions = [
    '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
    '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
    '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県',
    '岐阜県', '静岡県', '愛知県', '三重県',
    '滋賀県', '京都府', '大阪府', '兵庫県', '奈良県', '和歌山県',
    '鳥取県', '島根県', '岡山県', '広島県', '山口県',
    '徳島県', '香川県', '愛媛県', '高知県',
    '福岡県', '佐賀県', '長崎県', '熊本県', '大分県', '宮崎県', '鹿児島県',
    '沖縄県'
  ];

  // コメント投稿
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const settings = await getBoardSettings();
      setBoardSettingsState(settings);
      setTempUserName(settings.userName);
      setTempRegion(settings.region);
      setTempDefaultAnonymous(settings.defaultAnonymous);
      setIsAnonymous(settings.defaultAnonymous);
      
      await loadThreads();
    } catch (error) {
      console.error('データ読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadThreads = async () => {
    try {
      setLoading(true);
      const data = await getThreads();
      setThreads(data);

      // いいね状態を読み込み
      const liked = new Set<string>();
      for (const thread of data) {
        if (thread.id && await hasLikedThread(thread.id)) {
          liked.add(thread.id);
        }
      }
      setLikedThreads(liked);
    } catch (error) {
      console.error('スレッド読み込みエラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenThread = async (thread: Thread) => {
    try {
      if (!thread.id) return;
      
      const fullThread = await getThreadById(thread.id);
      if (!fullThread) return;
      
      setSelectedThread(fullThread);
      
      const commentData = await getComments(thread.id);
      setComments(commentData);

      // コメントのいいね状態を読み込み
      const liked = new Set<string>();
      for (const comment of commentData) {
        if (comment.id && await hasLikedComment(comment.id)) {
          liked.add(comment.id);
        }
      }
      setLikedComments(liked);
    } catch (error) {
      console.error('スレッド詳細読み込みエラー:', error);
    }
  };

  const handleCreateThread = async () => {
    try {
      if (!newTitle.trim() || !newContent.trim()) {
        onShowSnackbar('タイトルと本文を入力してください', 'error');
        return;
      }

      await createThread({
        category: newCategory,
        title: newTitle.trim(),
        content: newContent.trim(),
        isAnonymous,
        isPinned: false,
        createdAt: Date.now()
      });

      setShowNewThread(false);
      setNewTitle('');
      setNewContent('');
      setNewCategory('chat');
      setIsAnonymous(boardSettings.defaultAnonymous);
      
      onShowSnackbar('投稿しました');
      await loadThreads();
    } catch (error: any) {
      onShowSnackbar(error.message || '投稿に失敗しました', 'error');
    }
  };

  const handleOpenSettings = () => {
    setTempUserName(boardSettings.userName);
    setTempRegion(boardSettings.region);
    setTempDefaultAnonymous(boardSettings.defaultAnonymous);
    setShowSettings(true);
  };

  const handleSaveSettings = async () => {
    try {
      if (!tempUserName.trim()) {
        onShowSnackbar('表示名を入力してください', 'error');
        return;
      }

      if (!tempRegion.trim()) {
        onShowSnackbar('地域を選択してください', 'error');
        return;
      }

      const newSettings: BoardSettings = {
        userName: tempUserName.trim(),
        region: tempRegion.trim(),
        defaultAnonymous: tempDefaultAnonymous
      };

      await setBoardSettings(newSettings);
      setBoardSettingsState(newSettings);
      setShowSettings(false);
      onShowSnackbar('設定を保存しました');
    } catch (error) {
      onShowSnackbar('設定の保存に失敗しました', 'error');
    }
  };

  const handleCreateComment = async () => {
    try {
      if (!newComment.trim() || !selectedThread?.id) {
        onShowSnackbar('コメントを入力してください', 'error');
        return;
      }

      await createComment({
        threadId: selectedThread.id,
        content: newComment.trim()
      });

      setNewComment('');
      onShowSnackbar('コメントしました');
      
      // リロード
      if (selectedThread.id) {
        const commentData = await getComments(selectedThread.id);
        setComments(commentData);
        const updated = await getThreadById(selectedThread.id);
        if (updated) setSelectedThread(updated);
      }
    } catch (error) {
      onShowSnackbar('コメントに失敗しました', 'error');
    }
  };

  const handleLikeThread = async (threadId: string) => {
    try {
      await likeThread(threadId);
      setLikedThreads(prev => new Set(prev).add(threadId));
      await loadThreads();
      onShowSnackbar('いいねしました');
    } catch (error: any) {
      onShowSnackbar(error.message || 'いいねに失敗しました', 'error');
    }
  };

  const handleLikeComment = async (commentId: string) => {
    try {
      await likeComment(commentId);
      setLikedComments(prev => new Set(prev).add(commentId));
      
      if (selectedThread?.id) {
        const commentData = await getComments(selectedThread.id);
        setComments(commentData);
      }
      
      onShowSnackbar('いいねしました');
    } catch (error: any) {
      onShowSnackbar(error.message || 'いいねに失敗しました', 'error');
    }
  };

  const getCategoryLabel = (category: ThreadCategory) => {
    switch (category) {
      case 'chat': return '💬 雑談';
      case 'question': return '❓ 質問';
      case 'region': return '📍 地域情報';
      case 'tips': return '💡 お役立ち';
      case 'announce': return '📢 お知らせ';
    }
  };

  const getTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'たった今';
    if (minutes < 60) return `${minutes}分前`;
    if (hours < 24) return `${hours}時間前`;
    return `${days}日前`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0a0a0a' }}>
        <LinearProgress sx={{ width: '80%', bgcolor: '#1a4032', '& .MuiLinearProgress-bar': { bgcolor: '#10B981' } }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10, bgcolor: '#0a0a0a', minHeight: '100vh' }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="white" sx={{ mb: 0.5 }}>
            🏠 配達員掲示板
          </Typography>
          <Typography fontSize={14} color="#888">
            情報交換・雑談の場
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleOpenSettings}
            sx={{ color: '#888', '&:hover': { color: '#10B981' } }}
          >
            <Settings />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setShowNewThread(true)}
            sx={{
              bgcolor: '#10B981',
              color: 'black',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#0d9668' }
            }}
          >
            新規投稿
          </Button>
        </Box>
      </Box>

      {/* スレッド一覧 */}
      {selectedThread ? (
        /* スレッド詳細 */
        <Box>
          <Button
            onClick={() => setSelectedThread(null)}
            sx={{ color: '#10B981', mb: 2 }}
          >
            ← 一覧に戻る
          </Button>

          <Card sx={{ bgcolor: '#1a1a1a', borderRadius: '16px', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Chip
                label={getCategoryLabel(selectedThread.category)}
                size="small"
                sx={{ mb: 2, bgcolor: '#2a2a2a', color: '#10B981' }}
              />
              <Typography fontSize={20} fontWeight="bold" color="white" sx={{ mb: 2 }}>
                {selectedThread.title}
              </Typography>
              <Typography fontSize={16} color="#ccc" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {selectedThread.content}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Typography fontSize={14} color="#888">
                  {selectedThread.userName} [{selectedThread.region}]
                </Typography>
                <Typography fontSize={14} color="#666">
                  {getTimeAgo(selectedThread.createdAt)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => selectedThread.id && handleLikeThread(selectedThread.id)}
                  disabled={likedThreads.has(selectedThread.id || '')}
                  sx={{ color: likedThreads.has(selectedThread.id || '') ? '#10B981' : '#666' }}
                >
                  {selectedThread.likeCount}
                </Button>
                <Chip icon={<ChatBubbleOutline />} label={selectedThread.commentCount} size="small" sx={{ bgcolor: '#2a2a2a', color: '#888' }} />
                <Chip icon={<Visibility />} label={selectedThread.viewCount} size="small" sx={{ bgcolor: '#2a2a2a', color: '#888' }} />
              </Box>
            </CardContent>
          </Card>

          {/* コメント一覧 */}
          <Typography fontSize={17} fontWeight="bold" color="white" sx={{ mb: 2 }}>
            💬 コメント ({comments.length})
          </Typography>
          {comments.map((comment) => (
            <Card key={comment.id} sx={{ bgcolor: '#1a1a1a', borderRadius: '12px', mb: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography fontSize={15} color="#ccc" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                  {comment.content}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                  <Typography fontSize={13} color="#888">
                    {comment.userName} [{comment.region}]
                  </Typography>
                  <Typography fontSize={13} color="#666">
                    {getTimeAgo(comment.createdAt)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<ThumbUp />}
                  onClick={() => comment.id && handleLikeComment(comment.id)}
                  disabled={likedComments.has(comment.id || '')}
                  sx={{ 
                    color: likedComments.has(comment.id || '') ? '#10B981' : '#666',
                    fontSize: 11
                  }}
                >
                  {comment.likeCount}
                </Button>
              </CardContent>
            </Card>
          ))}

          {/* コメント投稿フォーム */}
          <Card sx={{ bgcolor: '#1a1a1a', borderRadius: '12px', mt: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="コメントを入力..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#10B981' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' }
                  }
                }}
              />
              <Button
                variant="contained"
                endIcon={<Send />}
                onClick={handleCreateComment}
                sx={{
                  bgcolor: '#10B981',
                  color: 'black',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#0d9668' }
                }}
              >
                コメント
              </Button>
            </CardContent>
          </Card>
        </Box>
      ) : (
        /* スレッド一覧 */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {threads.length === 0 ? (
            <Card sx={{ bgcolor: '#1a1a1a', borderRadius: '16px', p: 4 }}>
              <Box sx={{ textAlign: 'center', py: 4, color: '#888' }}>
                <Typography fontSize={16}>まだ投稿がありません</Typography>
              </Box>
            </Card>
          ) : (
            threads.map((thread) => (
              <Card
                key={thread.id}
                sx={{
                  bgcolor: '#1a1a1a',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}
                onClick={() => handleOpenThread(thread)}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip
                      label={getCategoryLabel(thread.category)}
                      size="small"
                      sx={{ bgcolor: '#2a2a2a', color: '#10B981', fontSize: 11 }}
                    />
                  </Box>
                  <Typography fontSize={17} fontWeight="bold" color="white" sx={{ mb: 1 }}>
                    {thread.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                    <Typography fontSize={14} color="#888">
                      {thread.userName} [{thread.region}]
                    </Typography>
                    <Typography fontSize={14} color="#666">
                      {getTimeAgo(thread.createdAt)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip icon={<ThumbUp />} label={thread.likeCount} size="small" sx={{ bgcolor: '#2a2a2a', color: '#888', fontSize: 11 }} />
                    <Chip icon={<ChatBubbleOutline />} label={thread.commentCount} size="small" sx={{ bgcolor: '#2a2a2a', color: '#888', fontSize: 11 }} />
                    <Chip icon={<Visibility />} label={thread.viewCount} size="small" sx={{ bgcolor: '#2a2a2a', color: '#888', fontSize: 11 }} />
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      {/* 新規投稿ダイアログ */}
      <Dialog
        open={showNewThread}
        onClose={() => setShowNewThread(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          📝 新規投稿
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="カテゴリ"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as ThreadCategory)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' }
              },
              '& .MuiInputLabel-root': { color: '#888' }
            }}
          >
            <MenuItem value="chat">💬 雑談</MenuItem>
            <MenuItem value="question">❓ 質問</MenuItem>
            <MenuItem value="region">📍 地域情報</MenuItem>
            <MenuItem value="tips">💡 お役立ち</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="タイトル"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="30文字まで"
            inputProps={{ maxLength: 30 }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' }
              },
              '& .MuiInputLabel-root': { color: '#888' }
            }}
          />

          <TextField
            fullWidth
            multiline
            rows={6}
            label="本文"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="500文字まで"
            inputProps={{ maxLength: 500 }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' }
              },
              '& .MuiInputLabel-root': { color: '#888' }
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <Typography fontSize={15} color="#888">
              匿名で投稿
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewThread(false)} sx={{ color: '#888' }}>
            キャンセル
          </Button>
          <Button onClick={handleCreateThread} sx={{ color: '#10B981', fontWeight: 'bold' }}>
            投稿
          </Button>
        </DialogActions>
      </Dialog>

      {/* 設定ダイアログ */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          ⚙️ 掲示板設定
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="表示名"
            value={tempUserName}
            onChange={(e) => setTempUserName(e.target.value)}
            placeholder="ニックネーム"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' }
              },
              '& .MuiInputLabel-root': { color: '#888' }
            }}
          />

          <TextField
            fullWidth
            select
            label="地域"
            value={tempRegion}
            onChange={(e) => setTempRegion(e.target.value)}
            SelectProps={{
              native: true,
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#10B981' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' }
              },
              '& .MuiInputLabel-root': { color: '#888' }
            }}
          >
            <option value="">選択してください</option>
            {regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <input
              type="checkbox"
              checked={tempDefaultAnonymous}
              onChange={(e) => setTempDefaultAnonymous(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <Typography fontSize={15} color="#888">
              デフォルトで匿名投稿
            </Typography>
          </Box>

          <Card sx={{ bgcolor: '#0a2318', border: '1px solid #10B981', borderRadius: '8px', p: 1.5 }}>
            <Typography fontSize={14} color="#ccc">
              💡 ランキング設定とは別に管理されます
            </Typography>
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)} sx={{ color: '#888' }}>
            キャンセル
          </Button>
          <Button onClick={handleSaveSettings} sx={{ color: '#10B981', fontWeight: 'bold' }}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BoardScreen;
